/**
 * Employee Injury Report Component
 * ===============================
 * 
 * Individual form component for employee injury and incident reporting.
 * 
 * Base Components:
 * - EXTENDS BaseFormComponent<EmployeeInjuryReport> for form logic inheritance
 * - USES FormLayoutComponent for step navigation UI (in template)
 * 
 * Form Configuration:
 * - Form structure, steps, sections, and field organization are defined in the form model
 * - Signature and upload requirements are configured via form model metadata
 * - Component implements abstract methods: initializeForm() and getFormData()
 * - Component overrides setupDefaultStepValidation() for custom step validation rules
 * 
 * Key Functionality:
 * - Creates nested form groups for logical organization (employeeInfo, jobInfo, incidentInfo, injuryInfo, additionalInfo)
 * - Auto-populates employee name fields with current user information
 * - Implements custom phone number validation with format enforcement
 * - Provides conditional field rendering based on incident type and injury status
 * - Implements step validation with signature requirements for final step
 * - Flattens nested form structure for backend submission
 * 
 * Unique Complex Logic:
 * - Conditional medical treatment contact information display when medical treatment is required
 * - Conditional previous injury description field based on previous condition selection
 * - Phone number validation with custom validator for proper format enforcement
 * - Auto-population of user information from authentication service
 * 
 * Form Model vs Component Responsibilities:
 * - FORM MODEL: Defines step/section structure, field mappings, attachment requirements, and form metadata
 * - COMPONENT: Handles Angular form validation, form group creation, conditional field logic, and overrides base form methods
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseFormComponent } from '../../base-form.component';
import { FormStep } from '../../form-layout.component';
import { EmployeeInjuryReport } from '../../../../../models/forms/employee-injury-report/employee-injury-report.model';
import { FormService } from '../../../../../services/form.service';
import { EntraService } from '../../../../../services/entra.service';
import { AttachmentUploadService } from '../../../../../services/attachment-upload.service';
import { SignatureService } from '../../../../../services/signature.service';
import { ToastService } from '../../../../../services/toast.service';
import { ScreenSizeService } from '../../../../../services/screen-size.service';
import { FormModelService } from '../../../../../services/form-model.service';
import { phoneNumberValidator } from '../../../../../utils/form-validators';


@Component({
  selector: 'app-employee-injury-report',
  templateUrl: './employee-injury-report.component.html',
  styleUrls: ['./employee-injury-report.component.css']
})
export class EmployeeInjuryReportComponent extends BaseFormComponent<EmployeeInjuryReport> implements OnInit {

  override formType = EmployeeInjuryReport.getFormInfo().formType;
  override model = new EmployeeInjuryReport();
  override steps: FormStep[] = EmployeeInjuryReport.getFormSteps();
  userNameFirst: string = '';
  userNameLast: string = '';

  // Form group getters
  get employeeInfoFormGroup(): FormGroup {
    return this.form.get('employeeInfo') as FormGroup;
  }

  get jobInfoFormGroup(): FormGroup {
    return this.form.get('jobInfo') as FormGroup;
  }

  get incidentInfoFormGroup(): FormGroup {
    return this.form.get('incidentInfo') as FormGroup;
  }

  get injuryInfoFormGroup(): FormGroup {
    return this.form.get('injuryInfo') as FormGroup;
  }

  get additionalInfoFormGroup(): FormGroup {
    return this.form.get('additionalInfo') as FormGroup;
  }

  constructor(
    protected override fb: FormBuilder,
    protected override formService: FormService,
    protected override router: Router,
    protected override entraService: EntraService,
    protected override attachmentService: AttachmentUploadService,
    protected override toastService: ToastService,
    protected override signatureService: SignatureService,
    protected override screenSizeService: ScreenSizeService,
    protected formModelService: FormModelService
  ) {
    super(fb, formService, router, entraService, attachmentService, toastService, signatureService, screenSizeService);


    this.formMetadata = this.formModelService.getFormMetadata(this.formType)!;
    this.userNameFirst = this.entraService.userFirstName();
    this.userNameLast = this.entraService.userLastName();
  }

  override ngOnInit(): void {
    super.ngOnInit();
  }

  protected override initializeForm(): void {
    this.form = this.fb.group({
      employeeInfo: this.fb.group({
        employeeFirstName: [this.userNameFirst, Validators.required],
        employeeLastName: [this.userNameLast, Validators.required],
        streetAddress: [this.model.streetAddress, Validators.required],
        city: [this.model.city, Validators.required],
        stateProvince: [this.model.stateProvince, Validators.required],
        zipPostalCode: [this.model.zipPostalCode, Validators.required],
        phone: [this.model.phone, [Validators.required, phoneNumberValidator()]],
        dateOfHire: [
          this.model.dateOfHire ? this.model.dateOfHire.toISOString() : null,
          Validators.required,
        ],
        craftAndPosition: [this.model.craftAndPosition, Validators.required],
      }),
      jobInfo: this.fb.group({
        shiftWorked: [this.model.shiftWorked, Validators.required],
        supervisorFirstName: [this.model.supervisorFirstName, Validators.required],
        supervisorLastName: [this.model.supervisorLastName, Validators.required],
        hoursWorkedPerDay: [this.model.hoursWorkedPerDay, Validators.required],
        hoursWorkedPerWeek: [this.model.hoursWorkedPerWeek, Validators.required],
      }),
      incidentInfo: this.fb.group({
        typeOfIncident: [this.model.typeOfIncident, Validators.required],
        dateOfIncident: [
          this.model.dateOfIncident ? this.model.dateOfIncident.toISOString() : null,
          Validators.required,
        ],
        timeOfIncident: [this.model.timeOfIncident, Validators.required],
        dateReported: [
          this.model.dateReported ? this.model.dateReported.toISOString() : null,
          Validators.required,
        ],
        timeReported: [this.model.timeReported, Validators.required],
        incidentReportedToFirstName: [
          this.model.incidentReportedToFirstName,
          Validators.required,
        ],
        incidentReportedToLastName: [
          this.model.incidentReportedToLastName,
          Validators.required,
        ],
        jobSiteOfInjury: [this.model.jobSiteOfInjury, Validators.required],
        exactLocationOfInjury: [this.model.exactLocationOfInjury, Validators.required],
        equipment1: [this.model.equipment1, Validators.required],
        equipment2: [this.model.equipment2],
        anyDefects: [this.model.anyDefects],
        coveredInPreTask: [this.model.coveredInPreTask, Validators.required],
        witness1: [this.model.witness1, Validators.required],
        witness2: [this.model.witness2],
        taskDescription: [this.model.taskDescription, Validators.required],
        howInjuryOccurred: [this.model.howInjuryOccurred, Validators.required],
        contributingFactors: [this.model.contributingFactors, Validators.required],
      }),
      injuryInfo: this.fb.group({
        ppeRequired: [this.model.ppeRequired, Validators.required],
        ppeWorn: [this.model.ppeWorn, Validators.required],
        adequateLighting: [this.model.adequateLighting, Validators.required],
        injured: [this.model.injured, Validators.required],
        bloodExposure: [this.model.bloodExposure, Validators.required],
        seekMedicalTreatment: [this.model.seekMedicalTreatment, Validators.required],
        natureOfInjury: [this.model.natureOfInjury, Validators.required],
        bodyPartInjured: [this.model.bodyPartInjured, Validators.required],
        injuryDescription: [this.model.injuryDescription, Validators.required],
        previousSimilarCondition: [this.model.previousSimilarCondition, Validators.required],
        previousInjuryDescription: [this.model.previousInjuryDescription],
      }),
      additionalInfo: this.fb.group({
        propertyDamage: [this.model.propertyDamage, Validators.required],
        othersInvolved: [this.model.othersInvolved, Validators.required],
        preventionSuggestion: [this.model.preventionSuggestion, Validators.required],
        employeeStatement: [this.model.employeeStatement],
        signatureDate: [
          this.model.signatureDate ? this.model.signatureDate.toISOString() : null,
          Validators.required,
        ],
      })
    });
  }

  protected override getFormData(): EmployeeInjuryReport {
    const formValue = this.form.value;
    // Flatten the nested form groups into a single object
    const flattenedData = {
      ...formValue.employeeInfo,
      ...formValue.jobInfo,
      ...formValue.incidentInfo,
      ...formValue.injuryInfo,
      ...formValue.additionalInfo
    };
    const employeeInjuryReport = new EmployeeInjuryReport(flattenedData);
    const updatedEmployeeInjuryReport = this.updateModelWithAttachments(employeeInjuryReport);
    return updatedEmployeeInjuryReport;
  }

   protected override setupDefaultStepValidation(): void {
    // Skip validation setup if noFormRequirements is true
    if (!this.shouldApplyFormRequirements()) {
      return;
    }
    // Define step indices
    const SIGNATURE_REQUIREMENT_INDEX: number = 0;

    // Custom validation rules for each step using the form groups directly
    this.setupStepValidation('employee-info', this.employeeInfoFormGroup);
    this.setupStepValidation('job-info', this.jobInfoFormGroup);
    this.setupStepValidation('incident-info', this.incidentInfoFormGroup);
    this.setupStepValidation('injury-info', this.injuryInfoFormGroup);
    this.setupStepValidation('additional-info', this.additionalInfoFormGroup, {
      signatureIndex: [SIGNATURE_REQUIREMENT_INDEX]
    });
  }
} 